import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "antd";
import { getSubscriptionsList } from "../../appRedux/actions/subscriptions";

// Helper to compute discount
const computeDiscountedPrice = (price, discountValue, discountType, maxDiscountAmount) => {
  const originalPrice = parseFloat(price);
  if (isNaN(originalPrice)) return null;

  const val = parseFloat(discountValue);
  if (isNaN(val) || val <= 0) return originalPrice;

  let discount = 0;
  if (discountType === 'PERCENT') {
    discount = originalPrice * (val / 100);
    const maxAmt = parseFloat(maxDiscountAmount);
    if (maxDiscountAmount && !isNaN(maxAmt)) {
      discount = Math.min(discount, maxAmt);
    }
  } else {
    discount = val;
  }

  return Math.max(0, originalPrice - discount);
};

function PreviewOffer({ info, visible, onClose }) {
  const dispatch = useDispatch();
  const subscriptionList = useSelector((state) => state.subscriptions.subscriptionList || []);

  useEffect(() => {
    if (visible && subscriptionList.length === 0) {
      dispatch(getSubscriptionsList({ size: 100 })).catch(() => {});
    }
  }, [visible, subscriptionList.length, dispatch]);

  const {
    title,
    description,
    bannerImageUrl,
    bannerText,
    stickerLabel,
    ctaText,
    ctaLink,
    couponCode,
    discountValue,
    discountType,
    maxDiscountAmount,
    level,
    linkedSubscriptions = [],
  } = info;

  // Calculate preview items for subscriptions
  const previewItems = [];
  if (level === "MERCHANT") {
    linkedSubscriptions.forEach((link) => {
      const targetId =
        typeof link.subscriptionId === "object" && link.subscriptionId !== null
          ? link.subscriptionId._id
          : link.subscriptionId;

      const sub = subscriptionList.find((s) => s._id === targetId);
      if (!sub) return;

      const options = sub.pricingOptions || [];
      options.forEach((opt) => {
        const identifier = opt._id || opt.name;
        const isSelected =
          link.pricingOptionIds.length === 0 || link.pricingOptionIds.includes(identifier);

        if (isSelected && opt.isActive !== false) {
          const originalPrice = parseFloat(opt.price);
          const hasPrice = (opt.priceType === "PAID" || !opt.priceType) && !isNaN(originalPrice);
          const finalPrice = hasPrice
            ? computeDiscountedPrice(
                originalPrice,
                discountValue,
                discountType,
                maxDiscountAmount
              )
            : null;

          previewItems.push({
            subName: sub.title,
            optName: opt.name || opt.billingCycle,
            priceType: opt.priceType,
            originalPrice,
            finalPrice,
            currency: opt.currency || "INR",
            billingCycle: opt.billingCycle,
          });
        }
      });
    });
  }

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={500}
      bodyStyle={{ padding: 0 }}
      centered
      destroyOnClose
    >
        <div className="live-preview-sidebar-card" style={{ border: "none", boxShadow: "none", margin: 0 }}>
          <div className="preview-header">
            <i className="fa fa-eye mr-2" /> Live Offer Preview
          </div>

          {/* Clean Banner Image Preview */}
          <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#f8fafc', marginBottom: '20px' }}>
            {bannerImageUrl ? (
              <img
                src={bannerImageUrl}
                alt="Banner Visual"
                style={{ width: '100%', maxHeight: '420px', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <div className="banner-mockup-bg default-gradient" style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'static' }}>
                <span style={{ color: '#ffffff', fontWeight: 600, fontSize: '15px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                  No Banner Image Created Yet
                </span>
              </div>
            )}
          </div>

          {/* Offer Details Mockup */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
            {(stickerLabel || bannerText) && (
              <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
                {stickerLabel && (
                  <span style={{ background: '#3b82f6', color: '#ffffff', fontSize: '10px', fontWeight: 800, padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {stickerLabel}
                  </span>
                )}
                {bannerText && (
                  <span style={{ background: '#ef4444', color: '#ffffff', fontSize: '10px', fontWeight: 800, padding: '3px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {bannerText}
                  </span>
                )}
              </div>
            )}
            <h4 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px 0', fontFamily: 'Outfit, sans-serif' }}>
              {title || "Offer Title Mockup"}
            </h4>
            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 16px 0', lineHeight: 1.5, fontFamily: 'Inter, sans-serif' }}>
              {description || "Add a description to make this offer stand out."}
            </p>
            <div>
              {ctaLink ? (
                <a href={ctaLink} target="_blank" rel="noopener noreferrer" className="banner-mockup-btn" style={{ position: 'static', margin: 0, border: '1px solid #cbd5e1', display: 'inline-flex', alignItems: 'center' }}>
                  {ctaText || "Learn More"} <i className="fa fa-external-link ml-1" style={{ fontSize: '10px' }} />
                </a>
              ) : (
                <button type="button" className="banner-mockup-btn" style={{ position: 'static', margin: 0, border: '1px solid #cbd5e1' }}>
                  {ctaText || "Learn More"}
                </button>
              )}
            </div>
          </div>

          {/* Coupon Code section */}
          {couponCode && (
            <div className="coupon-ticket-mockup">
              <div className="coupon-ticket-border-left" />
              <div className="coupon-ticket-body">
                <span className="coupon-ticket-label">COUPON CODE</span>
                <span className="coupon-ticket-code">{couponCode}</span>
                <span className="coupon-ticket-subtext">Apply code at checkout to claim discount.</span>
              </div>
              <div className="coupon-ticket-border-right" />
            </div>
          )}

          {/* Subscription levels preview */}
          {previewItems.length > 0 && (
            <div className="offer-preview-prices-container">
              <div className="prices-title">🎓 Pricing Preview for Learners</div>
              <div className="prices-grid">
                {previewItems.map((item, idx) => (
                  <div key={idx} className="price-item-card">
                    <div className="price-plan-name">{item.subName} - {item.optName}</div>
                    {item.priceType === 'FREE' ? (
                      <div className="price-new text-success">FREE</div>
                    ) : item.priceType === 'CONTACT_SALES' ? (
                      <div className="price-new text-warning">Contact Sales</div>
                    ) : (
                      <div>
                        {item.originalPrice !== null && (
                          <span className="price-old">{item.currency === 'INR' ? '₹' : item.currency}{item.originalPrice?.toLocaleString()}</span>
                        )}
                        <span className="price-new">
                          {item.currency === 'INR' ? '₹' : item.currency}{item.finalPrice?.toLocaleString()}
                          <span className="price-cycle">/{item.billingCycle.toLowerCase()}</span>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
  );
}

export default PreviewOffer;
